/**
 * 主题（浅色 / 深色 / 跟随系统）。
 *
 * 纯前端偏好，存在 localStorage，不进后端：它是「这台设备」的设置，
 * 手机上想要深色、办公室电脑上想要浅色是正常的。
 *
 * 生效方式是给 <html> 挂 data-theme，index.css 里按它覆盖颜色 token。
 * index.html 里有一段同样逻辑的内联脚本在首屏渲染前跑，避免先白后黑的闪烁 ——
 * 改这里的键名或取值时两边一起改。
 */

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'rex:theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // 隐私模式等场景下 localStorage 会抛，按默认处理
  }
  return 'system'
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light'
  return preference
}

/** 状态栏 / 地址栏的颜色：和页面底色一致，装到主屏幕后顶上不会多出一条色带 */
const THEME_COLOR: Record<ResolvedTheme, string> = { light: '#ffffff', dark: '#000000' }

export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference)
  document.documentElement.dataset.theme = resolved
  // 让原生控件（滚动条、表单）也跟着换
  document.documentElement.style.colorScheme = resolved
  // index.html 里的两条 theme-color 按系统深浅色区分；手动选了主题就两条都改成当前这一个
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => meta.setAttribute('content', THEME_COLOR[resolved]))
  return resolved
}

export function saveThemePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // 存不下就只对当前页面生效
  }
  applyTheme(preference)
}

/** 跟随系统时，系统切换深浅色要实时跟上。返回取消监听的函数。 */
export function watchSystemTheme(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {}
  const media = window.matchMedia(DARK_QUERY)
  media.addEventListener('change', onChange)
  return () => media.removeEventListener('change', onChange)
}
